import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useEditorNavigation } from "../stores/EditorNavigationContext";

/**
 * Pager link that runs editor before-navigate logic (e.g. submit new product)
 * then navigates. Use in place of PagerLink on About and Chapter pages.
 */
export default function EditorPagerLink({ to, direction, children }) {
  const { navigateTo } = useEditorNavigation();
  const dir = direction?.toLowerCase() || "";

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      navigateTo(to);
    },
    [to, navigateTo]
  );

  const isPrev = dir === "previous";
  const isNext = dir === "next";
  const dirLabel = dir
    ? `${dir.charAt(0).toUpperCase()}${dir.slice(1)}:`
    : "";

  return (
    <a
      href={to}
      aria-label={dirLabel ? `${dirLabel} ${children}` : undefined}
      className={`flex items-center gap-3 no-underline hover:opacity-75${isNext ? " ml-auto" : ""}`}
      data-direction={direction}
      onClick={handleClick}
    >
      {isPrev && (
        <span className="text-primary text-3xl leading-none" aria-hidden="true">
          ←
        </span>
      )}
      <span className={`block ${isNext ? "text-right" : "text-left"}`} aria-hidden="true">
        {dirLabel && (
          <span className="block text-sm text-gray-darker">{dirLabel}</span>
        )}
        <span className="block text-primary font-semibold">{children}</span>
      </span>
      {isNext && (
        <span className="text-primary text-3xl leading-none" aria-hidden="true">
          →
        </span>
      )}
    </a>
  );
}

EditorPagerLink.propTypes = {
  to: PropTypes.string.isRequired,
  direction: PropTypes.oneOf(["previous", "next"]),
  children: PropTypes.node.isRequired,
};

EditorPagerLink.defaultProps = {
  direction: "",
};
